import { CreateAllData } from '@/apiError/apiCreate';
import '@/assets/style/companyList.scss';

import LoadingOverlay from '@/utils/loading/loadingOverlay';

import {
  EyeInvisibleOutlined,
  EyeOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { Button, Form, Input, Modal } from 'antd';
import FocusTrap from 'focus-trap-react';
import { useCallback, useEffect, useRef, useState } from 'react'; // Improved import to include useCallback for performance optimization
import Draggable from 'react-draggable';
import { useNavigate } from 'react-router-dom';

/**
 * ChangePassword Component
 * Handles user password change with form validation, loading state, and notifications
 */
const ChangePassword = ({ visible, onCancel }) => {
  const navigate = useNavigate();

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  /**
   * Resets form fields and calls the onCancel callback
   */
  const handleCancel = useCallback(() => {
    form.resetFields();
    onCancel();
  }, [form, onCancel]);
  const modalRef = useRef(null);

  useEffect(() => {
    if (visible && modalRef.current) {
      modalRef.current.focus();
    }
  }, [visible]);

  /**
   * Handles form submission for changing the password
   * @param {Object} values - Form values containing old and new passwords
   */
  const onFinish = async (values) => {
    setLoading(true);
    try {
      await CreateAllData.ChangePassword('/user/change/password/', values);
      // Remove token and redirect to home page after a short delay
      setTimeout(() => {
        localStorage.removeItem('token');
        navigate('/');
      }, 700);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Draggable>
      <FocusTrap
        focusTrapOptions={{
          allowOutsideClick: (e) => true,
        }}
      >
        <Modal
          open={visible}
          onCancel={handleCancel}
          footer={null}
          closable={false}
          autoFocus={true}
          width={440}
          ref={modalRef}
          centered
          className="change-password-modal"
        >
          <LoadingOverlay loading={loading} />

          <div className="change-password-modal__header">
            <div className="change-password-modal__icon">
              <LockOutlined />
            </div>
            <div>
              <div className="change-password-modal__title">
                Change Password
              </div>
              <div className="change-password-modal__subtitle">
                Update your account password securely.
              </div>
            </div>
          </div>

          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            autoComplete="off"
          >
            <Form.Item
              className="username"
              label="Old password"
              name="oldPassword"
              rules={[
                {
                  required: true,
                  message: 'Please enter the old password.',
                },
                {
                  min: 6,
                  message: 'Password must be at least 6 characters.',
                },
                {
                  max: 20,
                  message: "Password can't exceed 20 characters.",
                },
              ]}
            >
              <Input.Password
                type="password"
                prefix={<LockOutlined className="cp-field-prefix" />}
                iconRender={(v) =>
                  v ? <EyeOutlined /> : <EyeInvisibleOutlined />
                }
              />
            </Form.Item>

            <Form.Item
              className="username"
              label="New password"
              name="newPassword"
              dependencies={['oldPassword']}
              rules={[
                {
                  required: true,
                  validator: (_, value) => {
                    if (!value) {
                      return Promise.reject('Please enter the new password.');
                    } else if (value.length < 6) {
                      return Promise.reject(
                        'Password must be at least 6 characters.',
                      );
                    } else if (value.length > 20) {
                      return Promise.reject(
                        "Password can't exceed 20 characters.",
                      );
                    } else if (value === form.getFieldValue('oldPassword')) {
                      return Promise.reject(
                        'New password must be different from old password.',
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input.Password
                type="password"
                prefix={<LockOutlined className="cp-field-prefix" />}
                iconRender={(visible) =>
                  visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                }
              />
            </Form.Item>

            <Form.Item
              className="username"
              label="Confirm new password"
              name="confirmNewPassword"
              dependencies={['newPassword']}
              rules={[
                {
                  required: true,
                  validator: (_, value) => {
                    if (!value) {
                      return Promise.reject(
                        'Please re-enter the new password.',
                      );
                    } else if (value.length < 6) {
                      return Promise.reject(
                        'Password must be at least 6 characters.',
                      );
                    } else if (value.length > 20) {
                      return Promise.reject(
                        "Password can't exceed 20 characters.",
                      );
                    } else if (value !== form.getFieldValue('newPassword')) {
                      return Promise.reject('The passwords do not match.');
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input.Password
                type="password"
                prefix={<LockOutlined className="cp-field-prefix" />}
                iconRender={(v) =>
                  v ? <EyeOutlined /> : <EyeInvisibleOutlined />
                }
              />
            </Form.Item>

            <div className="change-password-modal__actions">
              <Button
                className="cp-btn cp-btn--save"
                htmlType="submit"
                type="primary"
                loading={loading}
                block
              >
                Save
              </Button>
              <Button
                className="cp-btn cp-btn--cancel"
                onClick={handleCancel}
                block
              >
                Cancel
              </Button>
            </div>
          </Form>
        </Modal>
      </FocusTrap>
    </Draggable>
  );
};

export default ChangePassword;
